import { Fragment } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router';
import { ChevronDown } from 'lucide-react';

import { cn } from '#/lib/utils';
import { Button } from '#/modules/commonComponents/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '#/modules/commonComponents/dropdown-menu';
import { DESTRUCTIVE_SOLID_BUTTON_CLASS } from '#/modules/imsMgmt/receipts/shared/destructiveSolidButtonClass';
import {
  pickAvailableWorkflowExposedIds,
  resolveLgDesktopLayout,
  resolveMdDesktopLayout,
  resolveMobileLayout,
} from '#/modules/imsMgmt/receipts/shared/documentCommandBarLayout';

/**
 * FMS 詳情指令列（外露常用＋「更多」選單，手機收成「操作」）。
 * 版型同非庫存採購單 CommandBar；actionMap 由各單據組好後傳入。
 */
const ACTION_SHAPE = PropTypes.shape({
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  icon: PropTypes.elementType,
  to: PropTypes.string,
  onSelect: PropTypes.func,
  destructive: PropTypes.bool,
  disabled: PropTypes.bool,
  available: PropTypes.bool,
  showExposedIcon: PropTypes.bool,
  exposedVariant: PropTypes.string,
  exposedClassName: PropTypes.string,
});

function runAfterMenuClose(callback) {
  window.setTimeout(() => {
    callback?.();
  }, 0);
}

function ExposedActionButton({ action, isPending }) {
  const Icon = action.icon;
  const disabled = Boolean(isPending || action.disabled);
  const iconNode = action.showExposedIcon && Icon
    ? <Icon className="size-3.5" aria-hidden />
    : null;

  if (action.to && !disabled) {
    return (
      <Button
        asChild
        variant={action.exposedVariant || 'default'}
        className={cn(action.exposedClassName)}
      >
        <Link to={action.to} aria-label={action.label}>
          {iconNode}
          {action.label}
        </Link>
      </Button>
    );
  }

  return (
    <Button
      type="button"
      variant={action.destructive ? 'destructive' : (action.exposedVariant || 'default')}
      className={cn(
        action.destructive && DESTRUCTIVE_SOLID_BUTTON_CLASS,
        action.exposedClassName,
      )}
      disabled={disabled}
      onClick={() => action.onSelect?.()}
    >
      {iconNode}
      {action.label}
    </Button>
  );
}

ExposedActionButton.propTypes = {
  action: ACTION_SHAPE.isRequired,
  isPending: PropTypes.bool,
};

function ActionMenuItem({ action, isPending }) {
  const Icon = action.icon;
  const disabled = Boolean(isPending || action.disabled);
  const iconNode = Icon ? <Icon className="size-4" aria-hidden /> : null;

  if (action.to && !disabled) {
    return (
      <DropdownMenuItem
        asChild
        variant={action.destructive ? 'destructive' : 'default'}
      >
        <Link to={action.to}>
          {iconNode}
          {action.label}
        </Link>
      </DropdownMenuItem>
    );
  }

  return (
    <DropdownMenuItem
      variant={action.destructive ? 'destructive' : 'default'}
      disabled={disabled}
      onSelect={() => {
        if (disabled) {
          return;
        }
        runAfterMenuClose(action.onSelect);
      }}
    >
      {iconNode}
      {action.label}
    </DropdownMenuItem>
  );
}

ActionMenuItem.propTypes = {
  action: ACTION_SHAPE.isRequired,
  isPending: PropTypes.bool,
};

function ActionsMenu({
  triggerLabel,
  triggerAriaLabel,
  groups,
  isPending,
}) {
  if (!groups.length) {
    return null;
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="outline"
          disabled={isPending}
          aria-label={triggerAriaLabel || triggerLabel}
        >
          {triggerLabel}
          <ChevronDown className="size-3.5" aria-hidden />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-auto min-w-44">
        {groups.map((group, index) => (
          <Fragment key={group.key}>
            {index > 0 ? <DropdownMenuSeparator /> : null}
            <DropdownMenuLabel>{group.label}</DropdownMenuLabel>
            {group.items.map((action) => (
              <ActionMenuItem key={action.id} action={action} isPending={isPending} />
            ))}
          </Fragment>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

ActionsMenu.propTypes = {
  triggerLabel: PropTypes.string.isRequired,
  triggerAriaLabel: PropTypes.string,
  groups: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    items: PropTypes.arrayOf(ACTION_SHAPE).isRequired,
  })).isRequired,
  isPending: PropTypes.bool,
};

const FmsDocumentCommandBarActions = ({
  actionMap,
  actionGroups,
  workflowExposedOrder,
  isPending = false,
}) => {
  const getExposedActionIds = (map) => (
    pickAvailableWorkflowExposedIds(map, workflowExposedOrder)
  );
  const mobileLayout = resolveMobileLayout({ actionMap, actionGroups });
  const mdLayout = resolveMdDesktopLayout({ actionMap, actionGroups, getExposedActionIds });
  const lgLayout = resolveLgDesktopLayout({ actionMap, actionGroups, getExposedActionIds });

  function renderLayoutRow(
    layout,
    { triggerLabel = '更多', triggerAriaLabel = '更多操作' } = {},
  ) {
    return (
      <>
        {layout.flatActions.map((action) => (
          <ExposedActionButton key={action.id} action={action} isPending={isPending} />
        ))}
        {layout.showMoreMenu ? (
          <ActionsMenu
            triggerLabel={triggerLabel}
            triggerAriaLabel={triggerAriaLabel}
            groups={layout.menuGroups}
            isPending={isPending}
          />
        ) : null}
      </>
    );
  }

  return (
    <div className="flex items-center justify-end gap-2">
      <div className="flex items-center gap-2 md:hidden">
        {renderLayoutRow(mobileLayout, {
          triggerLabel: '操作',
          triggerAriaLabel: '單據操作',
        })}
      </div>
      <div className="hidden items-center gap-2 md:flex lg:hidden">
        {renderLayoutRow(mdLayout)}
      </div>
      <div className="hidden items-center gap-2 lg:flex">
        {renderLayoutRow(lgLayout)}
      </div>
    </div>
  );
};

FmsDocumentCommandBarActions.propTypes = {
  actionMap: PropTypes.objectOf(ACTION_SHAPE).isRequired,
  actionGroups: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    ids: PropTypes.arrayOf(PropTypes.string).isRequired,
  })).isRequired,
  workflowExposedOrder: PropTypes.arrayOf(PropTypes.string).isRequired,
  isPending: PropTypes.bool,
};

export default FmsDocumentCommandBarActions;
